import { format, startOfDay } from 'date-fns';
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

export const utcDatetimeToTimezoneDateString = (
  datetime: string,
  timezone: string,
) => format(utcToZonedTime(datetime, timezone), 'yyyy-MM-dd');

// Reservation dates are the property's calendar day, not instants. Returns that
// day pinned to UTC midnight so it can be formatted/diffed without a timezone
// round-trip — round-trips through zonedTimeToUtc misresolve wall times that
// fall inside a skipped DST hour (e.g. America/Santiago spring-forward), and
// utcToZonedTime reads a date-only value as a UTC instant (previous day at
// negative offsets). Naive inputs keep their literal date; explicit instants
// are resolved to their calendar day in the property timezone.
export const propertyLocalDate = (value: string, timezone: string): Date => {
  const trimmed = value.trim();
  const naive =
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?)?$/.exec(
      trimmed,
    );
  if (naive) {
    return new Date(
      Date.UTC(Number(naive[1]), Number(naive[2]) - 1, Number(naive[3])),
    );
  }
  if (/(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed)) {
    const day = formatInTimeZone(trimmed, timezone, 'yyyy-MM-dd');
    return new Date(`${day}T00:00:00Z`);
  }
  return zonedTimeToUtc(trimmed, timezone);
};

export const formatPropertyDate = (
  value: string,
  timezone: string,
  formatStr: string,
  locale?: Locale,
): string =>
  formatInTimeZone(propertyLocalDate(value, timezone), 'UTC', formatStr, {
    locale,
  });

// Converts a local date to a UTC date at same day and 00:00:00 time
export const convertLocalDateToUTCDate = (value?: Date) =>
  value ? zonedTimeToUtc(startOfDay(value), 'UTC') : null;

// Converts a UTC date to a local date at same day and 00:00:00 time
export const convertUTCDateToLocalDate = (utcDate: Date | string) => {
  if (!utcDate) {
    return null;
  }

  const dateObject = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;

  // Validate that the date object is valid
  if (isNaN(dateObject.getTime())) {
    return null;
  }

  const year = dateObject.getUTCFullYear();
  const month = dateObject.getUTCMonth();
  const day = dateObject.getUTCDate();

  return new Date(year, month, day);
};
